import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { label: "Home", to: "/", id: "home" },
  { label: "About Us", to: "/#about", id: "about" },
  { label: "Our Services", to: "/#services", id: "services" },
  { label: "Equipment Types", to: "/#equipment", id: "equipment" },
  { label: "Become a Carrier", to: "/become-a-carrier", id: "become-carrier" },
  { label: "FAQ", to: "/#faq", id: "faq" },
  { label: "Contact", to: "/contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled ? "bg-ink/90 backdrop-blur-xl border-gold/20 shadow-[0_10px_40px_rgba(0,0,0,0.6)]" : "bg-ink/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand-link">
          <Logo size="md" testId="nav-logo" />
          <span className="leading-none">
            <span className="font-accent block text-lg sm:text-xl font-bold gold-text tracking-wide">B&amp;H</span>
            <span className="block text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-zinc-400 group-hover:text-champagne transition-colors duration-300">
              Truck Dispatching
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
          {LINKS.map((l) => (
            <Link key={l.id} to={l.to} className="nav-link" data-testid={`nav-link-${l.id}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/become-a-carrier"
            data-testid="get-started-btn"
            className="hidden sm:inline-flex items-center bg-gold text-ink font-bold uppercase tracking-[0.15em] text-xs px-6 py-3 transition-[box-shadow,transform,background-color] duration-300 hover:bg-champagne hover:shadow-[0_0_25px_rgba(212,175,55,0.45)] hover:-translate-y-0.5"
          >
            Get Started
          </Link>
          <button
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="xl:hidden text-champagne p-2 border border-gold/30 hover:border-gold transition-colors duration-300"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>
      <div className="h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-ink/[0.98] backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 h-20 border-b border-gold/20">
              <Logo size="md" testId="mobile-menu-logo" />
              <button data-testid="mobile-menu-close-btn" aria-label="Close menu" onClick={() => setOpen(false)} className="text-champagne p-2 border border-gold/30">
                <X size={22} />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 sm:px-10 py-10" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="font-display block text-3xl uppercase font-semibold text-zinc-200 hover:text-gold transition-colors duration-300 py-2 border-b border-white/5"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="pt-8">
                <Link to="/become-a-carrier" data-testid="mobile-get-started-btn" className="btn-gold w-full">
                  Get Started
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
